import { create } from 'zustand';
import { clearScheduleDay, getTruckSchedule, setScheduleDay } from '../services/routeSchedule.service';
import type { DayOfWeek, TruckWeekSchedule } from '../types/routeSchedule.type';

const ALL_DAYS: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function emptyDayErrors(): Record<DayOfWeek, string | null> {
  return ALL_DAYS.reduce((acc, day) => {
    acc[day] = null;
    return acc;
  }, {} as Record<DayOfWeek, string | null>);
}

interface RouteScheduleState {
  schedule: TruckWeekSchedule | null;
  isLoading: boolean;
  error: string | null;
  /** Per-cell error state -- one failing day must not block the others. */
  dayErrors: Record<DayOfWeek, string | null>;
  clearError: () => void;
  clearDayError: (day: DayOfWeek) => void;
  fetchTruckSchedule: (truckId: string) => Promise<TruckWeekSchedule | null>;
  /**
   * Sets the full ordered list of routes for `day` in one call -- the
   * caller (`TruckRouteScheduleTab.tsx`) is responsible for recomputing
   * `routeIds` as the day's whole intended list (existing routes plus an
   * append, minus a removal, or reordered) before calling this; the store
   * itself does no diffing. Passing `[]` here is never correct -- use
   * `clearDay` to actually clear a day (the backend rejects an empty array
   * on this endpoint with 422).
   */
  setDay: (truckId: string, day: DayOfWeek, routeIds: string[]) => Promise<TruckWeekSchedule | null>;
  clearDay: (truckId: string, day: DayOfWeek) => Promise<boolean>;
}

function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

export const useRouteScheduleStore = create<RouteScheduleState>((set) => ({
  schedule: null,
  isLoading: false,
  error: null,
  dayErrors: emptyDayErrors(),
  clearError: () => set({ error: null }),
  clearDayError: (day) => set((state) => ({ dayErrors: { ...state.dayErrors, [day]: null } })),

  fetchTruckSchedule: async (truckId) => {
    set({ isLoading: true, error: null, dayErrors: emptyDayErrors() });
    try {
      const schedule = await getTruckSchedule(truckId);
      set({ schedule, isLoading: false });
      return schedule;
    } catch (error) {
      set({ isLoading: false, error: errorMessage(error, 'Failed to load route schedule') });
      return null;
    }
  },

  // The PUT response is the full updated week schedule (not just the one day), so a successful
  // set can patch `schedule` directly from the response -- same "local-patch from the mutation
  // response" convention as `route.store.ts`, just that here the response already IS the whole
  // new schedule. `routeIds` is the day's full ordered list, not a single id -- see `setDay`'s
  // interface docstring.
  setDay: async (truckId, day, routeIds) => {
    set((state) => ({ dayErrors: { ...state.dayErrors, [day]: null } }));
    try {
      const schedule = await setScheduleDay(truckId, day, routeIds);
      set({ schedule });
      return schedule;
    } catch (error) {
      set((state) => ({
        dayErrors: { ...state.dayErrors, [day]: errorMessage(error, "Failed to set this day's route") },
      }));
      return null;
    }
  },

  // DELETE returns a bare `{ ok: true }` -- no schema to patch `schedule` from -- so a successful
  // clear removes the day from `schedule.days` locally instead.
  clearDay: async (truckId, day) => {
    set((state) => ({ dayErrors: { ...state.dayErrors, [day]: null } }));
    try {
      await clearScheduleDay(truckId, day);
      set((state) => ({
        schedule: state.schedule
          ? { ...state.schedule, days: state.schedule.days.filter((entry) => entry.day_of_week !== day) }
          : state.schedule,
      }));
      return true;
    } catch (error) {
      set((state) => ({
        dayErrors: { ...state.dayErrors, [day]: errorMessage(error, 'Failed to clear this day') },
      }));
      return false;
    }
  },
}));
